import {tx} from '@cityofzion/neon-core'
import {Arg, ArrayArgType} from '@cityofzion/neon-dappkit-types'
import {$} from '@/facade'
import {EnvHelper} from '@/helpers/EnvHelper'
import {currentN3Network} from '@/helpers/NetworkModeRegistry'
import {NeoHelper} from '@/helpers/NeoHelper'
import {BridgeNetworkHelper} from '@/helpers/BridgeNetworkHelper'
import {
  SwapNeoUsingNeoProxyArgs,
  N3InvocationBuilder,
  StandardConvertArgs,
  InvocationBuilderResult,
  DepositTokenArgs,
  AddOrbitProjectArgs,
  FundOrbitProjectArgs,
  SetupOrbitArgs,
  StartOrbitRoundArgs,
  ClaimOrbitArgs,
  DistributeOrbitRewardArgs,
  EditOrbitProjectArgs,
  RemoveOrbitProjectArgs,
  OrbitOwnerArgs,
  RefundOrbitContractArgs,
  SetClaimableNEP17Args,
} from '@/model/wallets/invocationsBuilder/N3InvocationBuilder'
import {
  NeolineContractInvocationMulti,
  NeolineInvokeArguments,
} from '@/model/wallets/types/NeolineTypes'

class N3NeolineInvocationBuilder implements N3InvocationBuilder {
  claimGasInvocation(address: string): NeolineContractInvocationMulti {
    const from = String(address)
    const to = String(address)

    return {
      invokeArgs: [
        {
          operation: 'transfer',
          scriptHash: EnvHelper.VUE_APP_NEO_SCRIPT_HASH,
          args: [
            {
              type: 'Address',
              value: from,
            },
            {
              type: 'Address',
              value: to,
            },
            {
              type: 'Integer',
              value: '0',
            },
            {
              type: 'Any',
              value: null,
            },
          ],
        },
      ],
      fee: '0',
      signers: [
        {
          account: NeoHelper.getScriptHashFromAddress(
            $.walletAdapter.n3Address!
          ),
          scopes: tx.WitnessScope.CalledByEntry,
        },
      ],
    }
  }

  voteInvocation(
    candidatePublicKey: string | null
  ): NeolineContractInvocationMulti {
    const publicKeyArg: Arg = candidatePublicKey
      ? {
          type: 'PublicKey',
          value: `0${candidatePublicKey}`,
        }
      : {
          type: 'Any',
          value: null,
        }

    return {
      invokeArgs: [
        {
          operation: 'vote',
          scriptHash: EnvHelper.VUE_APP_NEO_SCRIPT_HASH,
          args: [
            {
              type: 'Hash160',
              value: $.walletAdapter.n3Address!,
            },
            publicKeyArg,
          ],
        },
      ],
      fee: '0',
      signers: [
        {
          account: NeoHelper.getScriptHashFromAddress(
            $.walletAdapter.n3Address!
          ),
          scopes: tx.WitnessScope.CalledByEntry,
        },
      ],
    }
  }

  swapNeoUsingNeoProxyInvocation({
    address,
    neoAmountIn,
    gasAmountOutMin,
  }: SwapNeoUsingNeoProxyArgs): NeolineContractInvocationMulti {
    return {
      invokeArgs: [
        {
          operation: 'swapNeo',
          scriptHash: EnvHelper.VUE_APP_NEO_PROXY_SCRIPT_HASH,
          args: [
            {
              type: 'Hash160',
              value: address,
            },
            {
              type: 'Integer',
              value: neoAmountIn,
            },
            {
              type: 'Integer',
              value: gasAmountOutMin,
            },
          ],
        },
      ],
      networkFeeOverride: 10000000,
      signers: [
        {
          account: EnvHelper.VUE_APP_NEO_PROXY_SCRIPT_HASH,
          scopes: tx.WitnessScope.Global,
        },
        {
          account: NeoHelper.getScriptHashFromAddress(address),
          scopes: tx.WitnessScope.Global,
          allowedContracts: [
            EnvHelper.VUE_APP_NEO_PROXY_SCRIPT_HASH,
            NeoHelper.getScriptHashFromAddress(address),
          ],
        },
      ],
    }
  }

  standardConvertInvocation({
    path,
    pairIds,
    amountToUse,
    minAmountOut,
  }: StandardConvertArgs): InvocationBuilderResult {
    const tokenToUse = path[0]
    const tokenToReceive = path[path.length - 1]

    const amountToUseFormatted = Number(amountToUse) * 10 ** tokenToUse.decimals

    const minimumReceivedFormatted = BigInt(
      (Number(minAmountOut) * 10 ** tokenToReceive.decimals).toFixed(0)
    )

    const scriptHashFromAddress = NeoHelper.getScriptHashFromAddress(
      $.walletAdapter.n3Address!
    )

    const invokeArgs = {
      invokeArgs: [
        {
          scriptHash: '0xf40f694362957d56801a8cef7e62a83f7f1b7b0f',
          operation: 'standardConvert',
          args: [
            {
              type: 'Hash160',
              value: scriptHashFromAddress,
            },
            {
              type: 'Integer',
              value: String(amountToUseFormatted),
            },
            {
              type: 'Integer',
              value: String(minimumReceivedFormatted),
            },
            {
              type: 'Array',
              value: path.map(flamingoToken => {
                return {
                  type: 'Hash160',
                  value: flamingoToken.hash,
                }
              }),
            },
            {
              type: 'Array',
              value: pairIds.map(id => {
                return {
                  type: 'Integer',
                  value: id,
                }
              }),
            },
          ],
        },
      ],
      signers: [
        {
          account: scriptHashFromAddress,
          scopes: tx.WitnessScope.CustomContracts,
          allowedContracts: [
            '0xec268e9c642b7d09d10fe658bcb1cc63c0895d4d',
            '0xf40f694362957d56801a8cef7e62a83f7f1b7b0f',
            ...path.map(flamingoToken => flamingoToken.hash),
          ],
        },
      ],
    }

    return invokeArgs as any
  }

  depositNativeInvocation(amount: string): InvocationBuilderResult {
    const from = NeoHelper.getScriptHashFromAddress($.walletAdapter.n3Address!)
    const to = $.walletAdapter.xAddress!
    const amountToUseFormatted = (Number(amount) * 10 ** 8).toFixed()
    const maxFee = '10000000'

    const invokeArgs = {
      invokeArgs: [
        {
          scriptHash: BridgeNetworkHelper.n3BridgeScriptHash(),
          operation: 'depositNative',
          args: [
            {
              type: 'Hash160',
              value: from,
            },
            {
              type: 'Hash160',
              value: to,
            },
            {
              type: 'Integer',
              value: amountToUseFormatted,
            },
            {
              type: 'Integer',
              value: maxFee,
            },
          ],
        },
      ],
      fee: '0.0001',
      signers: [
        {
          account: NeoHelper.getScriptHashFromAddress(
            $.walletAdapter.n3Address!
          ),
          scopes: tx.WitnessScope.CustomContracts,
          allowedContracts: [
            BridgeNetworkHelper.n3BridgeScriptHash(),
            EnvHelper.VUE_APP_GAS_SCRIPT_HASH,
            EnvHelper.VUE_APP_NEO_SCRIPT_HASH,
          ],
        },
      ],
      extraSystemFee: 0,
      extraNetworkFee: 0,
    } as any

    return invokeArgs
  }

  depositTokenInvocation({
    amount,
    tokenDecimals,
    tokenHash,
  }: DepositTokenArgs): InvocationBuilderResult {
    const from = NeoHelper.getScriptHashFromAddress($.walletAdapter.n3Address!)
    const to = $.walletAdapter.xAddress!
    const amountToUseFormatted = (
      Number(amount) *
      10 ** tokenDecimals
    ).toFixed()
    const maxFee = '10000000'

    const invocations = {
      invokeArgs: [
        {
          scriptHash: BridgeNetworkHelper.n3BridgeScriptHash(),
          operation: 'depositToken',
          args: [
            {
              type: 'Hash160',
              value: tokenHash,
            },
            {
              type: 'Hash160',
              value: from,
            },
            {
              type: 'Hash160',
              value: to,
            },
            {
              type: 'Integer',
              value: amountToUseFormatted,
            },
            {
              type: 'Integer',
              value: maxFee,
            },
          ],
        },
      ],
      fee: '0.0001',
      signers: [
        {
          account: NeoHelper.getScriptHashFromAddress(
            $.walletAdapter.n3Address!
          ),
          scopes: tx.WitnessScope.CustomContracts,
          allowedContracts: [
            BridgeNetworkHelper.n3BridgeScriptHash(),
            EnvHelper.VUE_APP_GAS_SCRIPT_HASH,
            EnvHelper.VUE_APP_NEO_SCRIPT_HASH,
            tokenHash,
          ],
        },
      ],
      extraSystemFee: 0,
      extraNetworkFee: 0,
    } as any

    return invocations
  }

  wrapNEOInvocation(amount: string): InvocationBuilderResult {
    const from = NeoHelper.getScriptHashFromAddress($.walletAdapter.n3Address!)
    const to = EnvHelper.VUE_APP_BNEO_SCRIPT_HASH

    const invocations = {
      invokeArgs: [
        {
          scriptHash: EnvHelper.VUE_APP_NEO_SCRIPT_HASH,
          operation: 'transfer',
          args: [
            {
              type: 'Address',
              value: from,
            },
            {
              type: 'Address',
              value: to,
            },
            {
              type: 'Integer',
              value: amount,
            },
            {
              type: 'Any',
              value: null,
            },
          ],
        },
      ],
      fee: '0',
      signers: [
        {
          account: NeoHelper.getScriptHashFromAddress(
            $.walletAdapter.n3Address!
          ),
          scopes: tx.WitnessScope.CalledByEntry,
        },
      ],
      extraSystemFee: 0,
      extraNetworkFee: 0,
    } as any

    return invocations
  }

  unwrapBNEOInvocation(amount: string): InvocationBuilderResult {
    const from = $.walletAdapter.n3Address!
    const to = 'NPmdLGJN47EddqYcxixdGMhtkr7Z5w4Aos'
    const amountFormatted = (Number(amount) * 10 ** 5).toFixed()

    const invocations = {
      invokeArgs: [
        {
          scriptHash: EnvHelper.VUE_APP_GAS_SCRIPT_HASH,
          operation: 'transfer',
          args: [
            {
              type: 'Address',
              value: from,
            },
            {
              type: 'Address',
              value: to,
            },
            {
              type: 'Integer',
              value: amountFormatted,
            },
            {
              type: 'Any',
              value: null,
            },
          ],
        },
      ],
      fee: '0',
      signers: [
        {
          account: NeoHelper.getScriptHashFromAddress(
            $.walletAdapter.n3Address!
          ),
          scopes: tx.WitnessScope.CalledByEntry,
        },
      ],
      extraSystemFee: 0,
      extraNetworkFee: 0,
    } as any

    return invocations
  }

  addOrbitProjectInvocation({
    projectScriptHash,
    tokenScriptHash,
    amount,
    assetType,
    eventName,
  }: AddOrbitProjectArgs): InvocationBuilderResult {
    const from = NeoHelper.getScriptHashFromAddress($.walletAdapter.n3Address!)

    return {
      invokeArgs: [
        {
          scriptHash: EnvHelper.orbitScriptHash(currentN3Network()),
          operation: 'addProject',
          args: [
            {
              type: 'Hash160',
              value: projectScriptHash,
            },
            {
              type: 'Hash160',
              value: tokenScriptHash,
            },
            {
              type: 'Integer',
              value: amount,
            },
            {
              type: 'Integer',
              value: assetType,
            },
            {
              type: 'String',
              value: eventName,
            },
          ],
        },
      ],
      signers: [
        {
          account: from,
          scopes: tx.WitnessScope.CalledByEntry,
        },
      ],
    }
  }

  claimOrbitInvocation({
    projectScriptHash,
    instanceId,
  }: ClaimOrbitArgs): InvocationBuilderResult {
    return {
      invokeArgs: [
        {
          scriptHash: EnvHelper.orbitScriptHash(currentN3Network()),
          operation: 'claim',
          args: [
            {
              type: 'Hash160',
              value: projectScriptHash,
            },
            {
              type: 'Integer',
              value: instanceId,
            },
          ],
        },
      ],
      signers: [
        {
          account: NeoHelper.getScriptHashFromAddress(
            $.walletAdapter.n3Address!
          ),
          scopes: tx.WitnessScope.CalledByEntry,
        },
      ],
    }
  }

  distributeRewardInvocation({
    roundNumber,
  }: DistributeOrbitRewardArgs): InvocationBuilderResult {
    return {
      invokeArgs: [
        {
          scriptHash: EnvHelper.orbitScriptHash(currentN3Network()),
          operation: 'distributeReward',
          args: [
            {
              type: 'Integer',
              value: roundNumber,
            },
          ],
        },
      ],
      signers: [
        {
          account: NeoHelper.getScriptHashFromAddress(
            $.walletAdapter.n3Address!
          ),
          scopes: tx.WitnessScope.CalledByEntry,
        },
      ],
    }
  }

  setClaimableNEP17Invocation({
    userScriptHash,
    projectScriptHash,
    amount,
  }: SetClaimableNEP17Args): InvocationBuilderResult {
    return {
      invokeArgs: [
        {
          scriptHash: EnvHelper.orbitScriptHash(currentN3Network()),
          operation: 'setClaimableNEP17',
          args: [
            {
              type: 'Hash160',
              value: userScriptHash,
            },
            {
              type: 'Hash160',
              value: projectScriptHash,
            },
            {
              type: 'Integer',
              value: amount,
            },
          ],
        },
      ],
      signers: [
        {
          account: NeoHelper.getScriptHashFromAddress(
            $.walletAdapter.n3Address!
          ),
          scopes: tx.WitnessScope.CalledByEntry,
        },
      ],
    }
  }

  setClaimableNEP17MultipleInvocation(
    data: SetClaimableNEP17Args[]
  ): InvocationBuilderResult {
    const invokeArgs: NeolineInvokeArguments[] = data.map(
      ({userScriptHash, projectScriptHash, amount}) => ({
        scriptHash: EnvHelper.orbitScriptHash(currentN3Network()),
        operation: 'setClaimableNEP17',
        args: [
          {type: 'Hash160', value: userScriptHash},
          {type: 'Hash160', value: projectScriptHash},
          {type: 'Integer', value: amount},
        ],
      })
    )

    return {
      invokeArgs,
      signers: [
        {
          account: NeoHelper.getScriptHashFromAddress(
            $.walletAdapter.n3Address!
          ),
          scopes: tx.WitnessScope.CalledByEntry,
        },
      ],
    }
  }

  editOrbitProjectInvocation({
    projectScriptHash,
    tokenScriptHash,
    amount,
    assetType,
    instanceId,
    eventName,
  }: EditOrbitProjectArgs): InvocationBuilderResult {
    const from = NeoHelper.getScriptHashFromAddress($.walletAdapter.n3Address!)

    return {
      invokeArgs: [
        {
          scriptHash: EnvHelper.orbitScriptHash(currentN3Network()),
          operation: 'editProject',
          args: [
            {
              type: 'Hash160',
              value: projectScriptHash,
            },
            {
              type: 'Integer',
              value: instanceId,
            },
            {
              type: 'Hash160',
              value: tokenScriptHash,
            },
            {
              type: 'Integer',
              value: amount,
            },
            {
              type: 'Integer',
              value: assetType,
            },
            {
              type: 'String',
              value: eventName,
            },
          ],
        },
      ],
      signers: [
        {
          account: from,
          scopes: tx.WitnessScope.CalledByEntry,
        },
      ],
    }
  }

  endOrbitRoundInvocation(): InvocationBuilderResult {
    const from = NeoHelper.getScriptHashFromAddress($.walletAdapter.n3Address!)

    return {
      invokeArgs: [
        {
          scriptHash: EnvHelper.orbitScriptHash(currentN3Network()),
          operation: 'endRound',
          args: [],
        },
      ],
      signers: [
        {
          account: from,
          scopes: tx.WitnessScope.CalledByEntry,
        },
      ],
    }
  }

  forceEndOrbitRoundInvocation(): InvocationBuilderResult {
    const from = NeoHelper.getScriptHashFromAddress($.walletAdapter.n3Address!)

    return {
      invokeArgs: [
        {
          scriptHash: EnvHelper.orbitScriptHash(currentN3Network()),
          operation: 'forceEndRound',
          args: [],
        },
      ],
      signers: [
        {
          account: from,
          scopes: tx.WitnessScope.CalledByEntry,
        },
      ],
    }
  }

  fundOrbitProjectsInvocation(
    data: FundOrbitProjectArgs[]
  ): InvocationBuilderResult {
    const from = NeoHelper.getScriptHashFromAddress($.walletAdapter.n3Address!)

    const contractInvocations: NeolineInvokeArguments[] = data.map(item => {
      return {
        scriptHash: item.tokenScriptHash,
        operation: 'transfer',
        args: [
          {
            type: 'Hash160',
            value: from,
          },
          {
            type: 'Hash160',
            value: EnvHelper.orbitScriptHash(currentN3Network()),
          },
          {
            type: 'Integer',
            value: item.amount,
          },
          {
            type: 'Array',
            value: [
              {
                type: 'Hash160',
                value: item.projectScriptHash,
              },
              {
                type: 'Integer',
                value: item.instanceId,
              },
            ],
          },
        ],
      }
    })

    return {
      invokeArgs: contractInvocations,
      signers: [
        {
          account: from,
          scopes: tx.WitnessScope.CalledByEntry,
        },
      ],
    }
  }

  pauseOrbitInvocation(): InvocationBuilderResult {
    const from = NeoHelper.getScriptHashFromAddress($.walletAdapter.n3Address!)

    return {
      invokeArgs: [
        {
          scriptHash: EnvHelper.orbitScriptHash(currentN3Network()),
          operation: 'pause',
          args: [],
        },
      ],
      signers: [
        {
          account: from,
          scopes: tx.WitnessScope.CalledByEntry,
        },
      ],
    }
  }

  removeOrbitProjectInvocation({
    projectScriptHash,
    instanceId,
  }: RemoveOrbitProjectArgs): InvocationBuilderResult {
    const from = NeoHelper.getScriptHashFromAddress($.walletAdapter.n3Address!)

    return {
      invokeArgs: [
        {
          scriptHash: EnvHelper.orbitScriptHash(currentN3Network()),
          operation: 'removeProject',
          args: [
            {
              type: 'Hash160',
              value: projectScriptHash,
            },
            {
              type: 'Integer',
              value: instanceId,
            },
          ],
        },
      ],
      signers: [
        {
          account: from,
          scopes: tx.WitnessScope.CalledByEntry,
        },
      ],
    }
  }

  setupOrbitInvocation({
    roundDuration,
    claimDuration,
    distributionDuration,
    lockoutDuration,
  }: SetupOrbitArgs): InvocationBuilderResult {
    const from = NeoHelper.getScriptHashFromAddress($.walletAdapter.n3Address!)

    return {
      invokeArgs: [
        {
          scriptHash: EnvHelper.orbitScriptHash(currentN3Network()),
          operation: 'setupOrbit',
          args: [
            {type: 'Integer', value: roundDuration},
            {type: 'Integer', value: claimDuration},
            {type: 'Integer', value: distributionDuration},
            {type: 'Integer', value: lockoutDuration},
          ],
        },
      ],
      signers: [
        {
          account: from,
          scopes: tx.WitnessScope.CalledByEntry,
        },
      ],
    }
  }

  startOrbitRoundInvocation({
    projectInstances,
  }: StartOrbitRoundArgs): InvocationBuilderResult {
    const from = NeoHelper.getScriptHashFromAddress($.walletAdapter.n3Address!)

    const argValue: ArrayArgType[] = projectInstances.map(projectInstance => {
      return {
        type: 'Array',
        value: [
          {
            type: 'Hash160',
            value: projectInstance.scriptHash,
          },
          {
            type: 'Integer',
            value: projectInstance.instanceId,
          },
        ],
      }
    })

    return {
      invokeArgs: [
        {
          scriptHash: EnvHelper.orbitScriptHash(currentN3Network()),
          operation: 'startRound',
          args: [
            {
              type: 'Array',
              value: argValue,
            },
          ],
        },
      ],
      signers: [
        {
          account: from,
          scopes: tx.WitnessScope.CalledByEntry,
        },
      ],
    }
  }

  unpauseOrbitInvocation(): InvocationBuilderResult {
    const from = NeoHelper.getScriptHashFromAddress($.walletAdapter.n3Address!)

    return {
      invokeArgs: [
        {
          scriptHash: EnvHelper.orbitScriptHash(currentN3Network()),
          operation: 'unpause',
          args: [],
        },
      ],
      signers: [
        {
          account: from,
          scopes: tx.WitnessScope.CalledByEntry,
        },
      ],
    }
  }

  addOrbitOwnerInvocation({
    ownerAddress,
  }: OrbitOwnerArgs): InvocationBuilderResult {
    const from = NeoHelper.getScriptHashFromAddress($.walletAdapter.n3Address!)

    return {
      invokeArgs: [
        {
          scriptHash: EnvHelper.orbitScriptHash(currentN3Network()),
          operation: 'addOwner',
          args: [
            {
              type: 'Hash160',
              value: ownerAddress,
            },
          ],
        },
      ],
      signers: [
        {
          account: from,
          scopes: tx.WitnessScope.CalledByEntry,
        },
      ],
    }
  }

  removeOrbitOwnerInvocation({
    ownerAddress,
  }: OrbitOwnerArgs): InvocationBuilderResult {
    const from = NeoHelper.getScriptHashFromAddress($.walletAdapter.n3Address!)

    return {
      invokeArgs: [
        {
          scriptHash: EnvHelper.orbitScriptHash(currentN3Network()),
          operation: 'removeOwner',
          args: [
            {
              type: 'Hash160',
              value: ownerAddress,
            },
          ],
        },
      ],
      signers: [
        {
          account: from,
          scopes: tx.WitnessScope.CalledByEntry,
        },
      ],
    }
  }

  refundOrbitContractInvocation({
    recipientAddress,
  }: RefundOrbitContractArgs): InvocationBuilderResult {
    const from = NeoHelper.getScriptHashFromAddress($.walletAdapter.n3Address!)

    return {
      invokeArgs: [
        {
          scriptHash: EnvHelper.orbitScriptHash(currentN3Network()),
          operation: 'refundContract',
          args: [
            {
              type: 'Hash160',
              value: recipientAddress,
            },
          ],
        },
      ],
      signers: [
        {
          account: from,
          scopes: tx.WitnessScope.CalledByEntry,
        },
      ],
    }
  }
}

export const n3NeolineInvocationBuilder = new N3NeolineInvocationBuilder()
